import { useMemo } from "react";
import { ThumbsDown, Clock3, Sparkles, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { formatDateOnly } from "@/utils/dateUtils";
import {
  useCampanhaSugestoes,
  type SinalCampanha,
  type SugestaoCadastroParado,
  type SugestaoCampanha,
  type SugestaoEngajado,
  type SugestaoQuaseAprovado,
} from "@/hooks/useCampanhaSugestoes";
import type { FreelancerSearchResult } from "@/services/campaigns-api";

interface SugestoesDestinatariosProps {
  selected: FreelancerSearchResult[];
  onAdd: (freelancer: FreelancerSearchResult) => void;
}

/** Descreve em texto curto por que o freelancer aparece na aba, sem jargão
 *  interno (nada de "golden record"/"sinal" na UI, só o resultado de negócio). */
function descreverQuaseAprovado(sugestao: SugestaoQuaseAprovado): string {
  const vagas = `Perdeu ${sugestao.vezesPreterido} vaga${sugestao.vezesPreterido > 1 ? "s" : ""}`;
  if (!sugestao.ultimaVagaEm) return vagas;
  return `${vagas}, última em ${formatDateOnly(sugestao.ultimaVagaEm, "MMM/yyyy")}`;
}

function descreverCadastroParado(sugestao: SugestaoCadastroParado): string {
  if (!sugestao.ultimaAtualizacaoPropriaEm) return "Nunca atualizou o cadastro por conta própria";
  return `Sem atualizar desde ${formatDateOnly(sugestao.ultimaAtualizacaoPropriaEm, "MMM/yyyy")}`;
}

function descreverEngajado(sugestao: SugestaoEngajado): string {
  const contato = sugestao.ultimoContatoEm
    ? `Contato em ${formatDateOnly(sugestao.ultimoContatoEm, "MMM/yyyy")}`
    : "Contato recente";
  if (sugestao.taxaClique && sugestao.taxaClique > 0) {
    return `${contato} · clicou em ${Math.round(sugestao.taxaClique * 100)}% das campanhas`;
  }
  return contato;
}

interface SugestaoListaProps<Sugestao extends SugestaoCampanha> {
  sinal: SinalCampanha;
  selected: FreelancerSearchResult[];
  onAdd: (freelancer: FreelancerSearchResult) => void;
  descrever: (sugestao: Sugestao) => string;
  tituloVazio: string;
}

function SugestaoLista<Sugestao extends SugestaoCampanha>({
  sinal,
  selected,
  onAdd,
  descrever,
  tituloVazio,
}: SugestaoListaProps<Sugestao>) {
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCampanhaSugestoes(sinal, true);

  const selectedEmails = useMemo(() => new Set(selected.map((f) => f.email)), [selected]);
  const sugestoes = (data?.pages.flatMap((pagina) => pagina.linhas) ?? []) as Sugestao[];
  const naoSelecionadas = sugestoes.filter((sugestao) => !selectedEmails.has(sugestao.email));

  if (isLoading) return <LoadingState variant="list" count={3} className="mt-2" />;

  if (isError) {
    return <ErrorState onRetry={() => refetch()} className="mt-2 p-6" />;
  }

  if (sugestoes.length === 0) {
    return (
      <EmptyState
        title={tituloVazio}
        description="Nenhum freelancer neste grupo agora."
        className="mt-2 p-6"
      />
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={naoSelecionadas.length === 0}
          onClick={() => naoSelecionadas.forEach((sugestao) => onAdd({ nome: sugestao.nome, email: sugestao.email }))}
        >
          Adicionar todos os visíveis
        </Button>
      </div>
      <ul className="max-h-56 divide-y overflow-auto rounded-md border">
        {sugestoes.map((sugestao) => {
          const jaSelecionado = selectedEmails.has(sugestao.email);
          return (
            <li
              key={sugestao.freelancerId}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{sugestao.nome || sugestao.email}</p>
                <p className="truncate text-xs text-muted-foreground">{descrever(sugestao)}</p>
              </div>
              <Button
                type="button"
                variant={jaSelecionado ? "secondary" : "outline"}
                size="sm"
                disabled={jaSelecionado}
                className="shrink-0"
                onClick={() => onAdd({ nome: sugestao.nome, email: sugestao.email })}
              >
                {jaSelecionado ? (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Selecionado
                  </>
                ) : (
                  <>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Adicionar
                  </>
                )}
              </Button>
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Carregando..." : "Mostrar mais"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Painel de sugestões de destinatários no fluxo manual de campanhas, uma aba
 * por sinal de negócio. Cada aba é alimentada por uma golden-record view
 * (sql/146) e reusa o mesmo formato de "selecionado" (FreelancerSearchResult)
 * do autocomplete manual — adicionar aqui é equivalente a escolher no
 * autocomplete, com dedup por email feito no próprio painel.
 */
export function SugestoesDestinatarios({ selected, onAdd }: SugestoesDestinatariosProps) {
  return (
    <div className="rounded-md border p-3">
      <p className="mb-2 text-sm font-medium">Sugestões</p>
      <Tabs defaultValue="quase_aprovados">
        <TabsList>
          <TabsTrigger value="quase_aprovados">
            <ThumbsDown className="h-3.5 w-3.5 mr-1.5" />
            Quase aprovados
          </TabsTrigger>
          <TabsTrigger value="cadastro_parado">
            <Clock3 className="h-3.5 w-3.5 mr-1.5" />
            Cadastro parado
          </TabsTrigger>
          <TabsTrigger value="engajados">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Engajados
          </TabsTrigger>
        </TabsList>
        <TabsContent value="quase_aprovados">
          <SugestaoLista<SugestaoQuaseAprovado>
            sinal="quase_aprovados"
            selected={selected}
            onAdd={onAdd}
            descrever={descreverQuaseAprovado}
            tituloVazio="Nenhum quase aprovado"
          />
        </TabsContent>
        <TabsContent value="cadastro_parado">
          <SugestaoLista<SugestaoCadastroParado>
            sinal="cadastro_parado"
            selected={selected}
            onAdd={onAdd}
            descrever={descreverCadastroParado}
            tituloVazio="Nenhum cadastro parado"
          />
        </TabsContent>
        <TabsContent value="engajados">
          <SugestaoLista<SugestaoEngajado>
            sinal="engajados"
            selected={selected}
            onAdd={onAdd}
            descrever={descreverEngajado}
            tituloVazio="Nenhum freelancer engajado"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default SugestoesDestinatarios;
